import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Scissors, Trash2 } from 'lucide-react';
import {
  SHOTS_STRATUM_ID, TICKS_PER_SECOND, parseTimecode, spriteTileAt, ticksToFrames, ticksToTimecodeString, framesToTicks,
  type AssetDetail, type Rational, type SegmentDTO, type SpriteLayout, type StratumDTO,
} from '@prism/contracts';
import { timecodeToMediaTicks } from '../lib/timecode-read';
import { api, errorMessage } from '../api/client';
import { useMe, useSegments, useStrata } from '../api/hooks';
import { toast } from '../components/Toasts';
import { queryKeys } from '../api/queryKeys';
import { FillFromShots, isEmptySegment } from './log/FillFromShots';
// 1.6.0 (A, log/Suggestions.tsx): AI suggestions with Accept and Reject.
import { SuggestionMark, SuggestionsBar, isPendingSuggestion } from './log/Suggestions';
import { spriteStyle } from '../components/SpriteThumb';
import { FollowButton } from '../components/FollowButton';
import { useFollowPlayhead } from '../lib/followList';
import { usePref } from '../lib/prefs';

const secondsToTicks = (s: number) => Math.round(s * TICKS_PER_SECOND);

/** The request body for a layer: tags layers are typed as one comma-separated line. */
function payload(stratum: StratumDTO, text: string, values: string[]) {
  if (stratum.kind === 'tags') return { text: '', values: text.split(',').map((v) => v.trim()).filter(Boolean) };
  return { text, values: values.map((v) => v.trim()).filter(Boolean) };
}

/**
 * Timecoded metadata: layers of segments with in and out points. Add a
 * segment from the player's in/out marks (I / O) or around the playhead.
 */
export function LogTab({ asset, currentSeconds, playing = false, onSeek, range }: {
  asset: AssetDetail; currentSeconds: number;
  /** 1.9.3: whether the player is playing (Follow: a play ends a pause made by scrolling by hand). */
  playing?: boolean;
  onSeek: (seconds: number) => void; range: () => { in: number | null; out: number | null } | undefined;
}) {
  const me = useMe().data!;
  const strata = (useStrata().data?.items ?? []).filter((s) => !s.archived);
  const allStrata = useStrata().data?.items ?? [];
  const segs = useSegments(asset.id);
  const qc = useQueryClient();
  const canEdit = me.permissions.includes('asset.edit_metadata') && !asset.deleted;
  const rate: Rational = asset.timing.sourceFrameRate ?? { num: 25, den: 1 };
  const startFrames = asset.timing.startTimecode ? parseTimecode(asset.timing.startTimecode, rate) ?? 0 : 0;
  const tc = (ticks: number) => ticksToTimecodeString(ticks, rate, startFrames);
  const duration = asset.durationTicks ?? 0;
  // Segments on hidden layers stay stored but are not shown.
  const items = (segs.data?.items ?? []).filter((i) => allStrata.some((s) => s.id === i.stratumId && !s.archived));
  const [filter, setFilter] = useState<string | null>(null);
  // 1.6.0: "Suggestions only" in the suggestions bar.
  const [suggestionsOnly, setSuggestionsOnly] = useState(false);

  const refresh = () => qc.invalidateQueries({ queryKey: queryKeys.segments(asset.id) });
  // 1.5.0: the Shots layer has a lane (read-only) but its segments are listed only when it is the chosen layer.
  const onLayer = filter ? items.filter((s) => s.stratumId === filter) : items.filter((s) => s.stratumId !== SHOTS_STRATUM_ID);
  const anySuggestion = items.some(isPendingSuggestion);
  const shown = suggestionsOnly && anySuggestion ? onLayer.filter(isPendingSuggestion) : onLayer;
  // 1.5.0 logging comfort: one segment open at a time; Enter saves it and opens the next empty one.
  const [editingId, setEditingId] = useState<string | null>(null);
  const openSegment = (seg: SegmentDTO | null) => {
    setEditingId(seg?.id ?? null);
    if (seg) onSeek(seg.startTicks / TICKS_PER_SECOND);
  };
  const nextEmpty = (after: SegmentDTO): SegmentDTO | null => {
    const later = shown.filter((x) => x.id !== after.id && x.startTicks >= after.startTicks && isEmptySegment(x) && !allStrata.find((st) => st.id === x.stratumId)?.system)
      .sort((a, b) => a.startTicks - b.startTicks);
    return later.find((x) => x.stratumId === after.stratumId) ?? later[0] ?? null;
  };
  const sprite = asset.representations.find((r) => r.kind === 'sprite');
  const keyframe = asset.media.spriteUrl && sprite ? { url: asset.media.spriteUrl, layout: sprite.attributes as unknown as SpriteLayout } : null;
  // 1.9.3: Follow keeps the segment at the playhead in view as it plays (the earliest of the shown ones, since segments
  // on different layers can overlap). Only the list scrolls (.log-tab .segment-list): the controls above it stay still.
  const now = secondsToTicks(currentSeconds);
  const isActive = (s: SegmentDTO) => now >= s.startTicks && now < s.endTicks;
  const followId = shown.filter(isActive).sort((a, b) => a.startTicks - b.startTicks)[0]?.id ?? null;
  const [follow, setFollow] = usePref('asset.log.follow', false);
  const list = useRef<HTMLDivElement>(null);
  useFollowPlayhead({ enabled: follow, playing, targetId: followId, list, attr: 'data-segment' });

  return (
    <div className="log-tab" data-testid="log-tab">
      <Lanes strata={strata} items={items} duration={duration} current={secondsToTicks(currentSeconds)} onSeek={onSeek} filter={filter} onFilter={setFilter} />
      {canEdit && strata.some((x) => !x.system) && (
        <AddSegment asset={asset} strata={strata.filter((x) => !x.system)} rate={rate} startFrames={startFrames} currentSeconds={currentSeconds} range={range} onAdded={refresh} tc={tc} />
      )}
      {/* 1.5.0 (S2): Fill from shots and Remove empty segments, per user layer. */}
      <FillFromShots asset={asset} strata={strata.filter((x) => !x.system)} segments={items} range={range} onChanged={refresh} tc={tc} />
      {/* 1.6.0 (A): AI suggestions on the layers, Accept all. */}
      <SuggestionsBar asset={asset} strata={strata.filter((x) => !x.system)} segments={items} canEdit={canEdit} onChanged={refresh} only={suggestionsOnly} onOnly={setSuggestionsOnly} />
      {strata.length === 0 && <p className="muted">No timecode layers are configured. An administrator can add them in Administration → Timecode layers.</p>}
      {segs.isLoading ? <div className="skeleton" style={{ height: 80 }} /> : shown.length === 0 ? (
        <p className="faint" style={{ marginTop: 12 }}>{items.length ? (suggestionsOnly && anySuggestion ? 'No AI suggestions on this layer.' : 'Nothing on this layer yet.') : 'No timecoded metadata yet. Mark in and out on the player (I / O) and describe what happens.'}</p>
      ) : (
        <>
        <div className="segment-list-head" data-testid="segment-list-head">
          <span className="faint">{shown.length} {shown.length === 1 ? 'segment' : 'segments'}</span>
          <FollowButton on={follow} onChange={setFollow} testId="log-follow" />
        </div>
        <div className="segment-list" ref={list} data-testid="segment-list">
          {shown.map((s) => <SegmentRow key={s.id} asset={asset} seg={s} stratum={allStrata.find((x) => x.id === s.stratumId)} tc={tc}
            canEdit={canEdit && !allStrata.find((x) => x.id === s.stratumId)?.system}
            active={isActive(s)} onSeek={onSeek} onChanged={refresh}
            editing={editingId === s.id} onOpen={() => openSegment(s)} onClose={() => setEditingId((id) => (id === s.id ? null : id))}
            onSavedNext={() => openSegment(nextEmpty(s))} keyframe={keyframe} onRefresh={refresh} />)}
        </div>
        </>
      )}
    </div>
  );
}

function Lanes({ strata, items, duration, current, onSeek, filter, onFilter }: {
  strata: StratumDTO[]; items: SegmentDTO[]; duration: number; current: number; onSeek: (s: number) => void; filter: string | null; onFilter: (id: string | null) => void;
}) {
  if (!duration || !strata.length) return null;
  const pct = (t: number) => `${Math.min(100, Math.max(0, (t / duration) * 100))}%`;
  return (
    <div className="lanes" aria-label="Timecode layers">
      {strata.map((s) => (
        <div className="lane" key={s.id}>
          <button className={`lane-label ${filter === s.id ? 'on' : ''}`} onClick={() => onFilter(filter === s.id ? null : s.id)} title={filter === s.id ? 'Show all layers' : `Show only ${s.label}${s.system ? ' (read-only)' : ''}`} data-testid={`lane-${s.id}`}>
            <span className="swatch" style={{ background: s.color }} />{s.label}
          </button>
          <div className="lane-track" onClick={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            onSeek(((e.clientX - r.left) / r.width) * duration / TICKS_PER_SECOND);
          }}>
            {items.filter((i) => i.stratumId === s.id).map((i) => (
              <span key={i.id} className="lane-seg" style={{ left: pct(i.startTicks), width: pct(i.endTicks - i.startTicks), background: s.color }}
                title={i.text || i.values.join(', ')} onClick={(e) => { e.stopPropagation(); onSeek(i.startTicks / TICKS_PER_SECOND); }} />
            ))}
            <span className="lane-playhead" style={{ left: pct(current) }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function ValueEditor({ stratum, text, values, onText, onValues, autoFocus = false }: { stratum: StratumDTO; text: string; values: string[]; onText: (t: string) => void; onValues: (v: string[]) => void; autoFocus?: boolean }) {
  if (stratum.kind === 'vocabulary') {
    return (
      <div className="tags" role="group" aria-label={stratum.label}>
        {stratum.options.map((o) => (
          <button type="button" key={o.value} className={`tag toggle ${values.includes(o.value) ? 'on' : ''}`} aria-pressed={values.includes(o.value)}
            onClick={() => onValues(values.includes(o.value) ? values.filter((v) => v !== o.value) : [...values, o.value])}>{o.label}</button>
        ))}
      </div>
    );
  }
  if (stratum.kind === 'tags') {
    // Typed as text; split into tags when saved.
    return <input className="input" value={text} placeholder="Names or keywords, comma-separated" aria-label={stratum.label} onChange={(e) => onText(e.target.value)} autoFocus={autoFocus} />;
  }
  return <textarea className="textarea" rows={2} value={text} onChange={(e) => onText(e.target.value)} placeholder="What happens here?" aria-label={stratum.label} autoFocus={autoFocus} />;
}

function AddSegment({ asset, strata, rate, startFrames, currentSeconds, range, onAdded, tc }: {
  asset: AssetDetail; strata: StratumDTO[]; rate: Rational; startFrames: number; currentSeconds: number;
  range: () => { in: number | null; out: number | null } | undefined; onAdded: () => void; tc: (t: number) => string;
}) {
  const [stratumId, setStratumId] = useState(strata[0]!.id);
  const [inTc, setInTc] = useState('');
  const [outTc, setOutTc] = useState('');
  const [text, setText] = useState('');
  const [values, setValues] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const stratum = strata.find((s) => s.id === stratumId) ?? strata[0]!;
  const toTicks = (value: string) => {
    // 1.9.1: a timecode earlier than the start is on the next day (a recording past midnight).
    return timecodeToMediaTicks(value, rate, startFrames, asset.durationTicks);
  };

  function applyMarks() {
    const r = range();
    const now = secondsToTicks(currentSeconds);
    const a = r?.in != null ? secondsToTicks(r.in) : now;
    const b = r?.out != null ? secondsToTicks(r.out) : Math.min(asset.durationTicks ?? a + 5 * TICKS_PER_SECOND, a + 5 * TICKS_PER_SECOND);
    setInTc(tc(a));
    setOutTc(tc(Math.max(b, a + framesToTicks(1, rate))));
  }

  async function add() {
    const startTicks = toTicks(inTc);
    const endTicks = toTicks(outTc);
    if (startTicks === null || endTicks === null) { toast('Enter in and out as timecode (HH:MM:SS:FF), or use the player marks', 'error'); return; }
    setBusy(true);
    try {
      await api(`/api/v1/assets/${asset.id}/segments`, { method: 'POST', json: { stratumId, startTicks, endTicks, ...payload(stratum, text, values) } });
      setText('');
      setValues([]);
      setInTc(outTc);
      setOutTc('');
      onAdded();
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="segment-add" data-testid="segment-add" onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); void add(); } }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr 1fr auto', gap: 6, alignItems: 'end' }}>
        <div className="field"><label htmlFor="seg-layer">Layer</label>
          <select id="seg-layer" className="select" value={stratumId} onChange={(e) => { setStratumId(e.target.value); setValues([]); }}>
            {strata.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>
        <div className="field"><label htmlFor="seg-in">In</label><input id="seg-in" className="input mono" value={inTc} placeholder={tc(secondsToTicks(currentSeconds))} onChange={(e) => setInTc(e.target.value)} /></div>
        <div className="field"><label htmlFor="seg-out">Out</label><input id="seg-out" className="input mono" value={outTc} placeholder="HH:MM:SS:FF" onChange={(e) => setOutTc(e.target.value)} /></div>
        <button className="btn sm" onClick={applyMarks} title="Use the player’s in and out marks (or 5 s from the playhead)"><Scissors size={13} /> Marks</button>
      </div>
      <ValueEditor stratum={stratum} text={text} values={values} onText={setText} onValues={setValues} />
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button className="btn primary sm" disabled={busy || !inTc || !outTc} onClick={() => void add()} data-testid="add-segment">
          {busy ? <Loader2 size={13} className="spin" /> : <Plus size={13} />} Add segment
        </button>
        <span className="faint" style={{ fontSize: 12 }}>⌘↵ adds · I / O on the player set marks</span>
      </div>
    </div>
  );
}

function SegmentRow({ asset, seg, stratum, tc, canEdit, active, onSeek, onChanged, editing, onOpen, onClose, onSavedNext, keyframe, onRefresh }: {
  asset: AssetDetail; seg: SegmentDTO; stratum: StratumDTO | undefined; tc: (t: number) => string; canEdit: boolean; active: boolean;
  onSeek: (s: number) => void; onChanged: () => void;
  /** 1.5.0: the open segment (one at a time, LogTab), opened with the player seeking to it. */
  editing: boolean; onOpen: () => void; onClose: () => void;
  /** Enter: saved, now open the next empty segment. */
  onSavedNext: () => void;
  /** The sprite, for the shot's keyframe beside the editor. */
  keyframe: { url: string; layout: SpriteLayout } | null;
  /** 1.6.0: refresh the list (an accepted or rejected suggestion). */
  onRefresh: () => void;
}) {
  const initialText = stratum?.kind === 'tags' ? seg.values.join(', ') : seg.text;
  const [text, setText] = useState(initialText);
  const [values, setValues] = useState(seg.values);
  const label = useMemo(() => (stratum?.kind === 'vocabulary' ? seg.values.map((v) => stratum.options.find((o) => o.value === v)?.label ?? v).join(', ') : seg.values.join(', ')), [seg.values, stratum]);
  const rowRef = useRef<HTMLDivElement>(null);
  // Opening starts from what is saved, and brings the row into view.
  useEffect(() => {
    if (!editing) return;
    setText(stratum?.kind === 'tags' ? seg.values.join(', ') : seg.text);
    setValues(seg.values);
    rowRef.current?.scrollIntoView?.({ block: 'nearest' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);
  const empty = isEmptySegment(seg);

  async function save(next = false) {
    try {
      const unchanged = text === initialText && values.join('\u0000') === seg.values.join('\u0000');
      // An untouched empty segment stays as it is (checkSegment wants text on a text layer); Enter just moves on.
      if (!(unchanged && empty)) {
        await api(`/api/v1/assets/${asset.id}/segments/${seg.id}`, { method: 'PATCH', json: stratum ? payload(stratum, text, values) : { text } });
        onChanged();
      }
      if (next) onSavedNext(); else onClose();
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }
  async function remove() {
    try {
      await api(`/api/v1/assets/${asset.id}/segments/${seg.id}`, { method: 'DELETE' });
      onChanged();
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  return (
    <div className={`segment-row ${active ? 'active' : ''} ${editing ? 'editing' : ''} ${seg.suggestedBy && !seg.acceptedAt ? 'suggestion' : ''}`} data-testid="segment-row" data-segment={seg.id} data-empty={empty || undefined}
      data-suggestion={(seg.suggestedBy && !seg.acceptedAt) || undefined} ref={rowRef}>
      <button className="segment-tc mono" onClick={() => onSeek(seg.startTicks / TICKS_PER_SECOND)} title="Play from here">
        {tc(seg.startTicks)}<span className="faint"> – {tc(seg.endTicks)}</span>
      </button>
      <div className="segment-body">
        <span className="segment-layer" style={{ color: stratum?.color }}>{stratum?.label ?? seg.stratumId}</span>
        {/* 1.6.0 (A): "AI suggestion" with Accept and Reject. */}
        <SuggestionMark asset={asset} seg={seg} canEdit={canEdit} onChanged={onRefresh} onOpen={onOpen} />
        {editing && stratum ? (
          <div className="segment-editor" data-testid="segment-editor"
            onKeyDown={(e) => {
              // Enter saves and opens the next empty segment; Shift+Enter is a new line; Esc closes.
              if (e.key === 'Enter' && !e.shiftKey && !e.altKey && !e.metaKey && !e.ctrlKey && !e.nativeEvent.isComposing) { e.preventDefault(); e.stopPropagation(); void save(true); }
              else if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
            }}>
            {keyframe && (
              <div className="segment-keyframe" aria-hidden data-testid="segment-keyframe"
                style={spriteStyle(keyframe.url, keyframe.layout, spriteTileAt(keyframe.layout, seg.startTicks / TICKS_PER_SECOND))} />
            )}
            <div className="segment-editor-main">
              <ValueEditor stratum={stratum} text={text} values={values} onText={setText} onValues={setValues} autoFocus />
              <div style={{ display: 'flex', gap: 6, marginTop: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                <button className="btn sm primary" onClick={() => void save()} data-testid="segment-save">Save</button>
                <button className="btn sm ghost" onClick={onClose}>Cancel</button>
                <span className="faint" style={{ fontSize: 11 }}>↵ saves and opens the next empty segment · ⇧↵ new line</span>
              </div>
            </div>
          </div>
        ) : empty && canEdit ? (
          <button type="button" className="link-button segment-empty" onClick={onOpen} data-testid="segment-open">Empty: log this shot</button>
        ) : (
          <span onDoubleClick={() => canEdit && onOpen()} title={canEdit ? 'Double-click to edit' : undefined}>
            {seg.text}{seg.text && label ? ' · ' : ''}{label && <span className="muted">{label}</span>}
          </span>
        )}
      </div>
      {canEdit && !editing && (
        <button className="btn ghost sm icon danger" aria-label="Remove segment" onClick={() => void remove()}><Trash2 size={12} /></button>
      )}
      <span className="faint" style={{ fontSize: 11 }}>{ticksToFrames(seg.endTicks - seg.startTicks, asset.timing.sourceFrameRate ?? { num: 25, den: 1 })}f</span>
    </div>
  );
}
